import AppBar from '@mui/material/AppBar'
import DialogContent from '@mui/material/DialogContent'
import Icon from '@mui/material/Icon'
import ExternalLink from '@mui/material/Link'
import { styled } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import React from 'react'
import Draggable from 'react-draggable'

import ContentLayout from '@/app/component/layout/content'
import AuthHeader from '@/app/component/section/header'
import CATIHeader from '@/app/component/section/header/cati'
import { PAGE } from '@/app/definitions'
import { CATI_USER_ROLE } from '@/app/module/cati/definitions'
import { useGetBalance } from '@/app/module/payments/hooks/use-get-balance'
import Plans from '@/app/module/plans'
import { useGetPlan } from '@/app/module/plans/hooks/use-get-plan'

const plansExcluded = [
  PAGE.ACCOUNT.BILLING.INDEX,
  PAGE.ACCOUNT.PROFILE.INDEX,
  PAGE.DASHBOARD.INDEX,
  PAGE.INVITATION,
  PAGE.PLANS.INDEX,
]

export default function Header(props: any) {
  const { trialMode, role, currentPage, activeOrg } = props

  const { data: balance } = useGetBalance()
  const { data: plan } = useGetPlan()
  const planId = plan?.planId

  const displayPlans =
    !plansExcluded.includes(currentPage) && ((plan && plan.trialDaysLeft < 0) || (planId === 0 && !trialMode))

  React.useEffect(() => {
    if (displayPlans) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [displayPlans])

  return (
    <>
      {displayPlans && (
        <PricingDialogContainer id="plans-dialog">
          <PricingDialogSubContainer>
            <DialogContent>
              <Plans showTrialMode={true} />
            </DialogContent>
          </PricingDialogSubContainer>
        </PricingDialogContainer>
      )}

      <StyledAppBar
        position="sticky"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <ContentLayout>
          <Toolbar
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: {
                xs: 0,
                sm: 0,
              },
            }}
          >
            {role === CATI_USER_ROLE ? <CATIHeader {...props} /> : <AuthHeader balance={balance} {...props} />}
          </Toolbar>
        </ContentLayout>
      </StyledAppBar>
      {planId === 0 && trialMode && currentPage !== PAGE.PLANS.INDEX && (
        <WarningContainer>
          <TrialSubContainer>
            <IconContainer>
              <Icon>notification_important</Icon>
            </IconContainer>
            <TrialTextContainer>
              <Typography variant="body1" color="textSecondary">
                You are currently in trial mode. Some platform features are restricted. To select a plan and learn more,{' '}
                <ExternalLink href="/plans">click here.</ExternalLink>
              </Typography>
            </TrialTextContainer>
          </TrialSubContainer>
        </WarningContainer>
      )}
      {role === 'customer_service' && (
        <WarningContainer>
          <Draggable axis="y">
            <CxSubContainer>
              <IconContainer>
                <Icon>notification_important</Icon>
              </IconContainer>
              <CxTextContainer>
                <CxText variant="h6" color="textPrimary">
                  You are in{' '}
                  <strong>
                    {activeOrg.name} ({activeOrg.orgId})
                  </strong>{' '}
                  with the <strong>CUSTOMER SUCCESS</strong> role. BE CAREFUL
                </CxText>
              </CxTextContainer>
            </CxSubContainer>
          </Draggable>
        </WarningContainer>
      )}
    </>
  )
}

const StyledAppBar = styled(AppBar)({
  color: '#fff',
  width: '100vw',
  maxWidth: '100%',
  left: 0,
})

const PricingDialogContainer = styled('div')({
  position: 'absolute',
  width: '100%',
  background: 'rgba(0, 0, 0, 0.57)',
  zIndex: 999,
  height: '100%',
  overflowY: 'scroll',
})

const PricingDialogSubContainer = styled('div')({
  background: 'white',
  width: '90%',
  margin: 'auto',
  marginTop: '80px',
  borderRadius: '4px',
  boxShadow: '0px 0px 0px 1px rgb(224 224 224)',
})

const WarningContainer = styled('div')({
  display: 'flex',
  textAlign: 'center',
  justifyContent: 'center',
  marginTop: '5px',
  marginBottom: '-10px',
  position: 'sticky',
  width: '100%',
  top: '90vh',
  zIndex: 1000,
  height: 0,
})

const TrialSubContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '3px',
  height: '40px',
  background: '#fff',
})

const IconContainer = styled('div')({
  height: '100%',
  justifySelf: 'center',
  display: 'flex',
  alignSelf: 'center',
  alignItems: 'center',
  padding: '7px',
  color: '#fff',
  background: '#ef6c00',
  borderTopLeftRadius: '3px',
  borderBottomLeftRadius: '3px',
})

const TrialTextContainer = styled('div')({
  border: '1px solid rgba(0, 0, 0, 0.44)',
  padding: '0 15px',
  borderRadius: '3px',
  borderLeft: 'none',
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  display: 'flex',
  height: '100%',
  alignItems: 'center',
})

const CxSubContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '3px',
  height: '40px',
  background: '#ef6c00',
  cursor: 'move',
})

const CxTextContainer = styled('div')({
  borderLeft: '1px solid #fff',
  padding: '0 15px',
  borderRadius: '3px',
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  display: 'flex',
  height: '100%',
  alignItems: 'center',
})

const CxText = styled(Typography)({
  color: '#fff',
})
